import { Path } from '../modules/Path.js';
import { ToArray } from '../modules/ToArray.js';

export function KatapultPicker(superClass) {
  return class extends superClass {
    static get properties() {
      return {
        autoValidate: {
          type: Boolean,
          value: false
        },
        externalChange: {
          type: Boolean,
          value: false
        },
        iconFunction: {
          type: Function
        },
        iconPath: {
          type: String,
          value: ''
        },
        invalid: {
          type: Boolean,
          value: false,
          notify: true,
          reflectToAttribute: true
        },
        items: {
          notify: true
        },
        itemsArray: {
          type: Array,
          value: () => [],
          notify: true
        },
        labelFunction: {
          type: Function
        },
        labelPath: {
          type: String,
          value: ''
        },
        required: {
          type: Boolean,
          value: false,
          notify: true
        },
        secondaryLabelFunction: {
          type: Function
        },
        secondaryLabelPath: {
          type: String,
          value: ''
        },
        selectedItem: {
          type: Object,
          value: null,
          notify: true
        },
        selectedItemLabel: {
          type: String,
          value: '',
          notify: true
        },
        value: {
          notify: true,
          observer: 'valueChanged'
        },
        _value: {
          type: String,
          observer: '_valueChanged'
        },
        valuePath: {
          type: String,
          value: ''
        }
      };
    }
    static get observers() {
      return [
        '_itemsArrayChanged(itemsArray)',
        'updateItemsArray(items.*)',
        '_updateSelectedItemLabel(selectedItem, labelPath, labelFunction)'
      ];
    }
    getIcon(item) {
      return ((this.iconFunction && this.iconFunction(item)) || (this.iconPath && Path.get(item, this.iconPath)) || '').toString();
    }
    getLabel(_item) {
      // Get subproperty of item if labelPath is set.
      const item = this.labelPath ? Path.get(_item, this.labelPath) : _item;
      // Get label from label function, falling back to label path
      let label = (this.labelFunction && this.labelFunction(item)) ?? item;
      // Check if we still don't have a label and get the value path in that case
      if (label == '' || label == null) {
        label = Path.get(_item, this.valuePath) ?? '';
      }
      return String(label);
    }
    getSecondaryLabel(item) {
      return (
        (this.secondaryLabelFunction && this.secondaryLabelFunction(item)) ||
        Path.get(item, this.secondaryLabelPath || this.valuePath) ||
        ''
      ).toString();
    }
    getValue(item) {
      let value = Path.get(item, this.valuePath || this.labelPath);
      if (value === null || value === undefined || value === '') value = this.nullValue;
      return value;
    }
    _itemsArrayChanged() {
      this._notifyExternalChange();
    }
    _notifyExternalChange() {
      let error;
      try {
        this.externalChange = true;
        this._updateValue();
      } catch (err) {
        error = err;
      } finally {
        this.externalChange = false;
      }
      if (error) throw error;
    }
    updateItemsArray() {
      this.itemsArray = ToArray(this.items);
    }
    _updateSelectedItemLabel() {
      this.selectedItemLabel = this.getLabel(this.selectedItem);
    }
    _updateValue() {
      if (this._value !== this.value) {
        let item = this.itemsArray.find((item) => this.getValue(item) === this.value);
        if (item || this.externalChange) this.setSelectedItem(item);
      }
    }
    valueChanged() {
      if (this._value !== this.value) {
        this._notifyExternalChange();
      }
      if (this.autoValidate) this.validate();
    }
    _valueChanged() {
      if (this._value !== this.value && !this.externalChange) {
        this.value = this._value;
      }
    }
    setSelectedItem(item) {
      // Notify selected will change event.
      if (!this.externalChange)
        this.dispatchEvent(
          new CustomEvent('selected-will-change', {
            detail: { value: this.getValue(item), selectedItem: item, previousValue: this.value },
            bubbles: true,
            composed: true
          })
        );
      // If prevent selection is set, don't select...duh
      if (this.preventSelection) {
        this.preventSelection = false;
      } else {
        // Update selected item.
        this.selectedItem = item;
        // Get the new value for _value from the selectedItem.
        let temp = this.getValue(this.selectedItem);
        // If _value and the new value are the same, then _valueChanged() won't run automatically.
        // So if this is the case manually call _valueChanged().
        if (this._value == temp) {
          // Manually call _valueChanged();
          this._valueChanged();
        } else {
          // Update _value.
          this._value = temp;
        }
        // Notify selected changed event.
        if (!this.externalChange)
          this.dispatchEvent(new CustomEvent('selected-changed', { detail: { value: this.value, selectedItem: this.selectedItem } }));
      }
    }
    validate() {
      this.invalid = this.required && (this.value == null || this.value == '');
      return !this.invalid;
    }
  };
}
